import './license-list.scss';

import { Box, Stack, Typography } from '@mui/material';
import {
  ContactType,
  DeleteBrokerageTeam,
  UserGroup,
  getPaginatedNodes,
} from '@converge/graphql';
import { DataGrid, GridColDef } from '@mui/x-data-grid';
import { IPerson, ITeams } from './accordion.form-container';

import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import { AddTeamModal } from './team-modal';
import { ApolloError } from '@apollo/client';
import { DeleteLicenseModal } from '../entity/broker-agencies/broker-form/delete-license-modal';
import HighlightOffIcon from '@mui/icons-material/HighlightOff';
import { LicenseModal } from './message-license-modal';
import { config } from '@converge/config';
import { useConvergeSnackBar } from '../../contexts';
import { useState } from 'react';
import BrokersByTeamDialog from './brokers-by-team-dialog';

export interface ITeamListProps {
  handleSubmitFromChild: () => void;

  brokerageUuid: string | undefined;
  brokerageTeams?: ITeams[];
  brokers?: IPerson[];
}

export interface ITeamUser {
  __typename?: 'UserEdges';
  edges: Array<{
    __typename?: 'UserEdge';
    cursor: number;
    node: {
      __typename?: 'User';
      uuid?: string | null | undefined;
      username?: string | null;
      userId?: string | null;
      groups?: UserGroup[] | null;
      type?: UserGroup | null;
    };
  }>;
}

export interface ISelectedTeams {
  id: string | undefined | null;
  teamName: string | undefined | null;
  uwRegion: string | undefined | null;
  uwRegionUuid?: string | undefined | null;
  primaryContact: string | undefined | null;
  primaryContactEmail: string | undefined | null;
  primaryContactEmailUuid?: string | undefined | null;
  businessDevelopmentManagerUuid?: string | undefined | null;
  underwriterUuid?: string | undefined | null;
  primaryContactUuid?: string | undefined | null;
  bdmRegionUuid?: string | undefined | null;
  bdmRegion: string | undefined | null;
}

export interface ISelectedDeleteTeam {
  teamUuid: string | null | undefined;
  brokerageUuid: string | null | undefined;
}

const TeamsList = ({
  handleSubmitFromChild,
  brokerageUuid,
  brokerageTeams,
  brokers,
}: ITeamListProps) => {
  const renderTableCell = (value: string, testId?: string) => (
    <Typography
      className="table-cell"
      data-testid={testId}
      sx={{
        width: '100%',
        paddingLeft: '100px',
      }}
    >
      {value || '—'}
    </Typography>
  );

  const [isBrokerageSave, setIsBrokerageSave] = useState(false);
  const [addNewTeam, setAddNewTeam] = useState(false);
  const [deleteModal, setDeleteModal] = useState(false);

  const [selectedDeleteTeam, setSelectedDeleteTeam] = useState<
    ISelectedDeleteTeam | null | undefined
  >(null);
  // eslint-disable-next-line @typescript-eslint/no-explicit-any

  const [selectedTeams, setSelectedTeams] = useState<
    ISelectedTeams | undefined | null
  >(null);

  // Dialog state for showing brokers by team
  const [dialogOpen, setDialogOpen] = useState(false);
  const [dialogTeamName, setDialogTeamName] = useState('');
  const [dialogTeamId, setDialogTeamId] = useState('');
  const { notify } = useConvergeSnackBar();
  const { loading, DeleteTeamFromEntity } = DeleteBrokerageTeam();

  const handleTeamClick = (teamName: string, teamId: string) => {
    if (teamName !== '—' && teamName !== '-' && teamId) {
      setDialogTeamName(teamName);
      setDialogTeamId(teamId);
      setDialogOpen(true);
    }
  };

  const handleDialogClose = () => {
    setDialogOpen(false);
    setDialogTeamName('');
    setDialogTeamId('');
  };

  const brokerageTeamList = brokerageTeams?.map((item) => {
    const node = item?.node;

    const underwriterUuid = node?.users?.edges?.filter(
      (underwriter) => underwriter?.node?.type === UserGroup.Underwriter
    )[0]?.node?.uuid;

    const businessDevelopmentManagerUuid = node?.users?.edges?.filter(
      (bDMuser) => bDMuser?.node?.type === UserGroup.Bdm
    )[0]?.node?.uuid;
    const uwRegion = Array.isArray(node?.uwTerritories)
      ? node?.uwTerritories
      : node?.uwTerritories?.edges ?? [];
    const bdmRegion = Array.isArray(node?.bdmTerritories)
      ? node?.bdmTerritories
      : node?.bdmTerritories?.edges ?? [];

    const uwTerritoriesName =
      uwRegion.length > 0 ? uwRegion[0]?.node?.name : '-';
    const uwTerritoriesUuid =
      uwRegion.length > 0 ? uwRegion[0]?.node?.uuid : '—';
    const BDMTerritoriesUuid =
      bdmRegion?.length > 0 ? bdmRegion[0]?.node?.uuid : '—';
    const BDMTerritoriesName =
      bdmRegion?.length > 0 ? bdmRegion[0]?.node?.name : '-';
    const persons = Array.isArray(node?.persons)
      ? node?.persons
      : node?.persons?.edges ?? [];
    const teamContact = getPaginatedNodes(node?.persons).filter(
      (contactInfo: unknown) =>
        (
          contactInfo as {
            teamLead?: boolean | null;
            firstName?: string;
            lastName?: string;
            uuid?: string;
            contacts?: {
              edges: Array<{
                node: {
                  type: string;
                  value?: string;
                };
              }>;
            };
          }
        )?.teamLead
    );

    const primaryContact =
      teamContact.length > 0
        ? `${(teamContact[0] as { firstName?: string })?.firstName} ${
            (teamContact[0] as { lastName?: string })?.lastName
          }`
        : '—';
    let primaryContactEmail = '—';

    // Look for the first contact with type 'email' if persons array is not empty
    if (persons.length > 0) {
      const contacts =
        (
          teamContact[0] as {
            contacts?: {
              edges: Array<{ node: { type: string; value?: string } }>;
            };
          }
        )?.contacts?.edges ?? [];
      // Find the first contact with type 'email'
      const emailContact = contacts.find(
        (contact: { node: { type: string } }) =>
          contact?.node?.type === ContactType.Email
      )?.node?.value;

      // If an email contact is found, assign it to primaryContactEmail
      if (emailContact) {
        primaryContactEmail = emailContact;
      }
    }
    return {
      id: node?.uuid ?? '—',
      teamName: node?.name ?? '—',
      uwRegion: uwTerritoriesName, // Add uwRegion logic here if needed
      primaryContact: primaryContact,
      primaryContactEmail: primaryContactEmail,
      primaryContactUuid: (teamContact[0] as { uuid?: string })?.uuid,
      uwRegionUuid: uwTerritoriesUuid,
      businessDevelopmentManagerUuid: businessDevelopmentManagerUuid,
      underwriterUuid: underwriterUuid,
      bdmRegion: BDMTerritoriesName,
      bdmRegionUuid: BDMTerritoriesUuid,
    };
  });

  const handleDelete = async () => {
    let LicenseResponseErrors;
    try {
      const { errors } = await DeleteTeamFromEntity({
        variables: {
          entity_uuid: selectedDeleteTeam?.brokerageUuid,
          team_uuid: selectedDeleteTeam?.teamUuid,
        },
      });
      LicenseResponseErrors = errors;

      notify(`Brokerage team has been deleted`, {
        variant: 'success',
      });
      setDeleteModal(false);
    } catch (cause) {
      console.error(`Failed to delete team. Please try again. (${cause}) `);
      if (cause instanceof ApolloError) {
        LicenseResponseErrors = cause.graphQLErrors;
      }
    }
    const errorMessage = LicenseResponseErrors?.[0]?.message;
    if (errorMessage) {
      notify(
        `Failed to delete team. Please try again. (${
          errorMessage || 'No data returned by the API'
        })`,
        {
          variant: 'error',
        }
      );
      return;
    }
  };

  const noData = [
    {
      id: 0,
      teamName: '—',
      uwRegion: '—',
      primaryContact: '—',
      primaryContactEmail: '—',
    },
  ];

  const columns: GridColDef[] = [
    {
      field: 'teamName',
      headerName: 'TEAM NAME',
      flex: 1.2,
      sortable: true,
      renderCell: (params) => {
        const teamName = params.value;
        const teamId = params.row.id;
        const isEmpty = teamName === '—' || teamName === '-' || !teamName;

        return (
          <Typography
            className="table-cell"
            data-testid={`teamName_${
              params.api.getRowIndexRelativeToVisibleRows(params.id) + 1
            }`}
            sx={{
              width: '100%',
              paddingLeft: '100px',
              cursor: isEmpty ? 'default' : 'pointer',
              color: 'inherit',
              textDecoration: isEmpty ? 'none' : 'underline',
              '&:hover': {
                backgroundColor: 'transparent',
              },
            }}
            onClick={
              isEmpty ? undefined : () => handleTeamClick(teamName, teamId)
            }
          >
            {teamName || '—'}
          </Typography>
        );
      },
    },
    {
      field: 'uwRegion',
      headerName: 'UW REGION',
      flex: 1.1,
      sortable: true,
      renderCell: (params) =>
        renderTableCell(
          params.value,
          `uwRegion_${
            params.api.getRowIndexRelativeToVisibleRows(params.id) + 1
          }`
        ),
    },
    {
      field: 'primaryContact',
      headerName: 'PRIMARY CONTACT',
      flex: 1.1,
      sortable: true,
      renderCell: (params) =>
        renderTableCell(
          params.value,
          `primaryContact_${
            params.api.getRowIndexRelativeToVisibleRows(params.id) + 1
          }`
        ),
    },
    {
      field: 'primaryContactEmail',
      headerName: 'PRIMARY CONTACT EMAIL',
      flex: 1.3,
      sortable: true,
      renderCell: (params) =>
        renderTableCell(
          params.value,
          `primaryContactEmail_${
            params.api.getRowIndexRelativeToVisibleRows(params.id) + 1
          }`
        ),
    },

    {
      field: 'iconColumn',
      headerName: '',
      flex: 0.9,
      sortable: false,
      renderCell: (params) => (
        <>
          <Typography
            className="table-cell icons"
            aria-label="info"
            sx={{
              display: 'flex',
              justifyContent: 'flex-end',
              gap: '1px',
              width: '100%',
              paddingLeft: '20px',
              paddingRight: '20px',
            }}
          >
            {brokerageTeamList?.length ? (
              <>
                <img
                  src={config.editIcon}
                  className="edit-icon"
                  alt="edit-icon"
                  data-testid={`edit-team-button_${
                    params.api.getRowIndexRelativeToVisibleRows(params.id) + 1
                  }`}
                  onClick={() => {
                    const selected = brokerageTeamList?.find(
                      (item) => item.id === params.id
                    );
                    if (selected) {
                      setSelectedTeams(selected);
                      setAddNewTeam(true);
                    }
                  }}
                />
                <HighlightOffIcon
                  sx={{ cursor: 'pointer', marginTop: '8px' }}
                  data-testid={`delete-team-button_${
                    params.api.getRowIndexRelativeToVisibleRows(params.id) + 1
                  }`}
                  onClick={() => {
                    setDeleteModal(true);
                    setSelectedDeleteTeam({
                      teamUuid: params.id.toString(),
                      brokerageUuid: brokerageUuid,
                    });
                  }}
                />
              </>
            ) : (
              ''
            )}
          </Typography>
        </>
      ),
    },
  ];

  return (
    <>
      <Box
        style={{
          height:
            brokerageTeamList && brokerageTeamList?.length > 10 ? '500px' : '',
        }}
      >
        <DataGrid
          className="team-list"
          rowHeight={36}
          disableVirtualization={true}
          sx={{
            border: 'none',
            overscrollBehavior: 'none',
            '& .MuiDataGrid-main': {
              borderRadius: '3px',
              border: '1px solid #cccccc',
              width: '89%',
              paddingBottom: '10px',
              height: 'auto',
              display: 'block',
              alignItems: 'center' /* Center vertically */,
              justifyContent: 'center',
              margin: 'auto',
              overflowX: 'hidden',
              overflowY: 'auto',
            },
            '& .MuiDataGrid-menuIcon': {
              display: 'none', // Hide the menu icon
            },
            '& .MuiDataGrid-root': {
              borderColor: 'transparent',
              border: 'none', // Remove the outer border around the DataGrid
            },
            '& .MuiDataGrid-root  .MuiDataGrid-row.Mui-selected': {
              backgroundColor: 'none !important',
            },
            '& .MuiDataGrid-cell.Mui-selected': {
              backgroundColor: 'none !important',
            },
            '& .MuiDataGrid-row.Mui-selected:hover': {
              backgroundColor: 'none !important',
            },
            '& .MuiDataGrid-row:hover': {
              backgroundColor: 'none !important',
              border: 'none', // Remove the outer border around the DataGrid
            },
            '& .MuiDataGrid-cell:hover': {
              backgroundColor: 'none !important',
              border: 'none', // Remove the outer border around the DataGrid
            },
            '& .Mui-selected': {
              backgroundColor: 'inherit', // Removes the selected background color for cells
            },
            '& .MuiDataGrid-row': {
              borderBottom: '1px solid rgba(224, 224, 224, 1)',
              backgroundColor: '#fff !important',
              borderRadius: '3px',
              fontSize: '14px',
              height: '36px !important',

              fontWeight: 500,
              marginTop: '0p',
              textAlign: 'center',
              marginBottom: '2px !important',
              '&:hover': {
                borderBottom: '1px solid rgba(224, 224, 224, 1) !important',
              },
            },
            '& .MuiDataGrid-cell': {
              display: 'flex',
              borderTop: 'none',
              fontWeight: '500',
              padding: '10px 0',
              alignItems: 'center',
              justifyContent: 'left',
              '& .MuiTypography-root': {
                width: '100%',
                paddingLeft: '20px',
                paddingRight: '20px',
              },
            },
            '& .MuiDataGrid-container--top [role="row"], & .MuiDataGrid-container--bottom [role="row"]':
              {
                backgroundColor: '#cefcf6',
                fontSize: '14px',
                fontWeight: 800,
                marginBottom: '0px',
              },

            '& .MuiDataGrid-columnHeader': {
              border: 'none !important',
              height: '32px !important',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'left',
              cursor: 'default',
              outline: 'none !important',
              userSelect: 'none',
              padding: '10px 0',
              '& .MuiDataGrid-columnHeaderTitleContainer': {
                width: '100%',
                paddingLeft: '20px',
                paddingRight: '20px',
              },
              '&:focus, &:focus-within': {
                outline: 'none !important',
              },
            },
            '& .MuiDataGrid-columnHeaderTitleContainer': {
              display: 'flex',
              alignItems: 'left',
              justifyContent: 'left',
            },

            '& .MuiDataGrid-sortIcon': {
              transform: 'translateY(3px)',
            },

            '& .MuiDataGrid-columnHeaderTitle': {
              fontWeight: '600', // Make header text bold
              fontSize: '12px',
              lineHeight: '15.88px',
              letterSpacing: '0.5px',
            },
            '& .MuiDataGrid-virtualScroller': {
              overflowX: 'hidden',
              overflowY: 'auto',
              margin: '0px',
            },
            '& .MuiDataGrid-virtualScrollerRenderZone': {
              width: '100%',
              paddingBottom: '10px',
            },
            '& .MuiDataGrid-columnSeparator--sideRight': {
              display: 'none',
            },
            '& .MuiDataGrid-footerContainer': {
              display: 'none',
            },
            '& .MuiDataGrid-selectedRowCount': {
              display: 'none', // Hide the "1 row selected" text
            },
            '& .MuiDataGrid-cell:focus': {
              outline: 'none', // Remove the default outline on focus
              background: 'none',
            },
            '& .MuiDataGrid-cell:focus-within': {
              outline: 'none', // Remove the default outline when focus is within the cell
            },
          }}
          rows={brokerageTeamList?.length ? brokerageTeamList : noData}
          columns={columns}
        />
      </Box>
      <Stack
        className="add-License-container"
        data-testid="add-team-button"
        onClick={() => {
          if (brokerageUuid !== undefined) {
            setAddNewTeam(true);
          } else {
            setIsBrokerageSave(true);
          }
        }}
      >
        <AddCircleOutlineIcon className="add-circle-style" /> ADD TEAMS
      </Stack>
      {isBrokerageSave && (
        <Stack>
          <LicenseModal
            title={'Save Before Adding Teams'}
            onClose={() => {
              setIsBrokerageSave(false);
            }}
            licenseSubmitHandler={() => {
              handleSubmitFromChild();
              setIsBrokerageSave(false);
            }}
            submitLabel="Save Brokerage"
            context={
              'Please save your changes before adding teams to this brokerage.'
            }
          />
        </Stack>
      )}
      {addNewTeam && (
        <Stack>
          <AddTeamModal
            title={selectedTeams ? 'Update Team ' : 'Add Team'}
            selectedTeams={selectedTeams}
            brokers={brokers}
            onClose={() => {
              setAddNewTeam(false);
              setSelectedTeams(null);
            }}
          />
        </Stack>
      )}

      {deleteModal && (
        <Stack>
          <DeleteLicenseModal
            DeleteHandler={() => {
              handleDelete();
            }}
            title={'Delete Team Information'}
            onClose={() => {
              setDeleteModal(false);
            }}
            submitLabel="DELETE TEAM"
            context={'Are you sure you want to delete this team information?'}
            loading={loading}
          />
        </Stack>
      )}

      {/* Dialog for showing brokers by team */}
      <BrokersByTeamDialog
        open={dialogOpen}
        onClose={handleDialogClose}
        teamName={dialogTeamName}
        teamId={dialogTeamId}
        entityId={brokerageUuid}
      />
    </>
  );
};

export default TeamsList;
